import React, { forwardRef } from 'react';
import { TinyColor } from '@ctrl/tinycolor';
import { css, useTheme } from '@emotion/react';
import { type CSSInterpolation } from '@emotion/serialize';
import { cs } from '@bbkAdminUtils/css-utils';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';

export const useBaseInputStyles = () => {
  const theme = useTheme() as EmotionTheme;
  const borderColor = new TinyColor(theme.textColor)
    .setAlpha(0.4)
    .toHexString();

  return css({
    border: `2px solid ${borderColor}`,
    padding: '5px',
    backgroundColor: 'transparent',
    color: theme.textColor,
    fontSize: '1rem',
    outline: 'none',
    borderRadius: theme.textAreaBorderRadius,
    fontFamily: theme.fontFamily,
    boxSizing: 'border-box',
    // Emotion object styles need the explicit `&` parent reference for pseudo-elements; a bare
    // `::placeholder` key was not being applied, so the placeholder never picked up these styles.
    '&::placeholder': {
      color: `${theme.textColor} !important`,
      opacity: 0.4,
    },
  });
};

type Props = React.InputHTMLAttributes<HTMLInputElement> & {
  classes?: string;
  fullWidth?: boolean;
  additionalStyles?: CSSInterpolation;
};

export const FunnelInput = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const { fullWidth = true, additionalStyles, classes, ...restProps } = props;

  const baseInputStyles = useBaseInputStyles();

  const inputStyles = {
    width: fullWidth ? '100%' : 'auto',
    height: '42px',
  };

  return (
    <input
      type="text"
      ref={ref}
      css={[baseInputStyles, inputStyles, additionalStyles]}
      className={cs(classes, 'bbk-funnel-input')}
      {...restProps}
    />
  );
});
